import React from 'react';
import { Habit } from '../../types';
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Edit2 } from 'lucide-react';

interface Props {
  habit: Habit;
  isCompleted: boolean;
  onToggle: (habitId: number, date: string) => void;
  onEdit: (habit: Habit) => void;
  currentDate: string; // YYYY-MM-DD format
}

export default function HabitItem({ habit, isCompleted, onToggle, onEdit, currentDate }: Props) {
  const handleToggle = () => {
    onToggle(habit.id, currentDate);
  };

  const handleEdit = () => {
    onEdit(habit);
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4 gap-2">
          <div className="flex items-center gap-3 flex-grow">
            <Checkbox
              id={`habit-${habit.id}`}
              checked={isCompleted}
              onCheckedChange={handleToggle}
              aria-label={`Mark habit ${habit.name} as ${isCompleted ? 'incomplete' : 'complete'}`}
            />
            <div>
              <label
                htmlFor={`habit-${habit.id}`}
                className={`text-sm font-medium leading-none cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-70 ${isCompleted ? "line-through text-muted-foreground" : ""}`}
              >
                {habit.name}
              </label>
              {habit.description && (
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <p className="text-xs text-muted-foreground mt-1 truncate cursor-help">
                        {habit.description}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs">
                      <p className="text-sm">{habit.description}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0" onClick={handleEdit} aria-label={`Edit habit ${habit.name}`}>
            <Edit2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
} 